// Stable serialization and content hashing for efmodel documents.
import type { JsonValue } from '../ef/values';
import type { EfModelDocument, ModelSpec } from './schema';

/** Deep copy with object keys sorted, for hashing and order-insensitive comparison. */
export function sortKeysDeep<T>(value: T): T {
  if (Array.isArray(value)) return value.map(sortKeysDeep) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as object).sort()) {
      const v = (value as Record<string, unknown>)[k];
      if (v !== undefined) out[k] = sortKeysDeep(v);
    }
    return out as T;
  }
  return value;
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortKeysDeep(value));
}

/** cyrb53: fast 53-bit string hash. Not cryptographic; used to detect identical content. */
export function hash53(text: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}

/** Identifies a model's content, ignoring key order and physical/extension details. */
export function modelHash(model: ModelSpec, options: { includePhysical?: boolean } = {}): string {
  const content = options.includePhysical ? model : stripPhysical(model);
  return `h53:${hash53(canonicalJson(content))}`;
}

/** Removes `physical` and `extensions` blocks at every level. */
export function stripPhysical<T>(value: T): T {
  if (Array.isArray(value)) return value.map(stripPhysical) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as object)) {
      if (k === 'physical' || k === 'extensions') continue;
      out[k] = stripPhysical(v);
    }
    return out as T;
  }
  return value;
}

/** Pretty JSON with the document's own key order, ending in a newline. */
export function serializeDocument(doc: EfModelDocument): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

export type { JsonValue };
